import { Button, ButtonProps } from "@chakra-ui/react";
import { forwardRef, ReactElement } from "react";

type CommonButtonVariant = "primary" | "secondary";

export interface CommonButtonProps
  extends Omit<ButtonProps, "variant" | "leftIcon" | "rightIcon"> {
  /**
   * Visual style of the button. Defaults to `primary`.
   */
  variant?: CommonButtonVariant;
  /**
   * Icon rendered at the start of the button label.
   */
  startIcon?: ReactElement;
  /**
   * Icon rendered at the end of the button label.
   */
  endIcon?: ReactElement;
}

const VARIANT_STYLES: Record<CommonButtonVariant, ButtonProps> = {
  primary: {
    bg: "#1A1A1A",
    color: "white.100",
    borderRadius: "4px",
    fontWeight: 500,
    fontSize: "14px",
    _hover: {
      bg: "black.101",
      _disabled: {
        bg: "black.100",
      },
    },
    _active: {
      bg: "black.200",
    },
  },
  secondary: {
    bg: "#EBEBEB",
    color: "black.100",
    borderRadius: "4px",
    borderWidth: "1px",
    borderColor: "gray.100",
    fontWeight: 500,
    fontSize: "14px",
    _hover: {
      bg: "#EBEBEB",
      _disabled: {
        bg: "white.100",
      },
    },
    _active: {
      bg: "gray.200",
    },
  },
};

export const CommonButton = forwardRef<HTMLButtonElement, CommonButtonProps>(
  (
    {
      variant = "primary",
      startIcon,
      endIcon,
      children,
      iconSpacing = 2,
      borderRadius = "12px",
      fontWeight = "600",
      fontSize = "16px",
      lineHeight = "24px",
      height = "44px",
      px = 6,
      ...rest
    },
    ref
  ) => {
    const { primary, secondary } = VARIANT_STYLES;
    const variantStyles = variant === "secondary" ? secondary : primary;

    return (
      <Button
        ref={ref}
        leftIcon={startIcon}
        rightIcon={endIcon}
        iconSpacing={iconSpacing}
        borderRadius={borderRadius}
        fontWeight={fontWeight}
        fontSize={fontSize}
        lineHeight={lineHeight}
        height={height}
        px={px}
        {...variantStyles}
        {...rest}
        flexShrink={0}
        whiteSpace="nowrap"
        minW="max-content"
      >
        {children}
      </Button>
    );
  }
);

CommonButton.displayName = "CommonButton";
